import { useState, useMemo, useCallback, type KeyboardEvent, type PointerEvent, type MouseEvent } from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { ChevronRight, ChevronDown, GripVertical, Search, Pencil } from "lucide-react";
import {
  getNavSectionDisplayLabel,
  getNavItemDisplayLabel,
  NAV_SECTION_HEADING_KEYS,
} from "@workspace/api-zod/quote-headings";
import { COST_NAV_SECTIONS, type CostNavItem, type CostNavSection } from "@/lib/cost-library-nav";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";

// Re-exported for existing imports; the shared module owns the mapping now.
export { getNavSectionDisplayLabel, NAV_SECTION_HEADING_KEYS };

const SECTION_ORDER_KEY = "costLibrarySectionOrder";

function loadSectionOrder(): string[] | null {
  try {
    const stored = localStorage.getItem(SECTION_ORDER_KEY);
    if (stored) return JSON.parse(stored);
  } catch {}
  return null;
}

function saveSectionOrder(order: string[]) {
  try {
    localStorage.setItem(SECTION_ORDER_KEY, JSON.stringify(order));
  } catch {}
}

export function useOrderedSections(catalogueSections: readonly CostNavSection[] = COST_NAV_SECTIONS) {
  const [orderedIds, setOrderedIds] = useState<string[]>(() => {
    const saved = loadSectionOrder();
    return saved ?? [];
  });

  const effectiveIds = useMemo(() => {
    const available = new Set(catalogueSections.map(section => section.id));
    const saved = orderedIds.filter(id => available.has(id));
    return [...saved, ...catalogueSections.map(section => section.id).filter(id => !saved.includes(id))];
  }, [catalogueSections, orderedIds]);

  const sections = useMemo(() => {
    const map = new Map(catalogueSections.map((s) => [s.id, s]));
    return effectiveIds.map((id) => map.get(id)).filter(Boolean) as CostNavSection[];
  }, [catalogueSections, effectiveIds]);

  const reorder = useCallback((activeId: string, overId: string) => {
    setOrderedIds(() => {
      const oldIndex = effectiveIds.indexOf(activeId);
      const newIndex = effectiveIds.indexOf(overId);
      if (oldIndex === -1 || newIndex === -1) return effectiveIds;
      const next = arrayMove(effectiveIds, oldIndex, newIndex);
      saveSectionOrder(next);
      return next;
    });
  }, [effectiveIds]);

  return { sections, orderedIds: effectiveIds, reorder };
}

function EditNameButton({ label, testId, onEdit }: { label: string; testId: string; onEdit: () => void }) {
  // Sibling of the row button (never nested). Stops pointer/key events so the
  // row never selects, collapses or starts a drag when the pencil is used.
  const stop = (e: PointerEvent | MouseEvent | KeyboardEvent) => e.stopPropagation();
  return (
    <button
      type="button"
      aria-label={`Rename ${label}`}
      title={`Rename ${label}`}
      data-testid={testId}
      onPointerDown={stop}
      onMouseDown={stop}
      onKeyDown={stop}
      onClick={(e) => {
        e.stopPropagation();
        e.preventDefault();
        onEdit();
      }}
      className="shrink-0 self-start mt-1.5 mr-2 inline-flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground/70 hover:text-primary hover:bg-primary/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring transition-colors"
    >
      <Pencil className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
  );
}

function SortableSectionItem({
  section,
  isExpanded,
  isSearching,
  selectedItemId,
  onToggle,
  onSelectSection,
  onSelectItem,
  displayLabel,
  headingNames,
  onEditName,
}: {
  displayLabel: string;
  headingNames?: Record<string, string> | null;
  onEditName?: (sectionId: string, itemId?: string) => void;
  section: CostNavSection;
  isExpanded: boolean;
  isSearching: boolean;
  selectedItemId: string | null;
  onToggle: () => void;
  onSelectSection: (section: CostNavSection) => void;
  onSelectItem: (item: CostNavItem, sectionId: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.id, disabled: isSearching });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : undefined,
    position: isDragging ? ("relative" as const) : undefined,
    opacity: isDragging ? 0.9 : 1,
  };

  const SectionIcon = section.icon;

  return (
    <div ref={setNodeRef} style={style} data-testid={`section-${section.id}`}>
      <div className="flex items-start group">
        <div
          className={`flex items-center justify-center w-6 pl-1 self-stretch touch-none ${
            isSearching
              ? "invisible"
              : "cursor-grab active:cursor-grabbing text-muted-foreground/40"
          }`}
          data-testid={`drag-handle-${section.id}`}
          aria-label={`Drag to reorder ${displayLabel}`}
          role="button"
          tabIndex={isSearching ? -1 : 0}
          {...(isSearching ? {} : { ...attributes, ...listeners })}
        >
          <GripVertical className="h-4 w-4" />
        </div>
        <button
          onClick={() => {
            if (!isSearching) onToggle();
            onSelectSection(section);
          }}
          className="flex-1 min-w-0 flex items-center gap-2 pl-1 pr-2 py-2.5 text-left hover-elevate transition-colors"
          data-testid={`button-section-${section.id}`}
        >
          <SectionIcon className="h-5 w-5 text-primary flex-shrink-0" />
          <span className="text-xs font-semibold uppercase tracking-wider text-primary flex-1 min-w-0 break-words [overflow-wrap:anywhere] leading-snug">
            {displayLabel}
          </span>
          {!isSearching &&
            (isExpanded ? (
              <ChevronDown className="h-4 w-4 text-muted-foreground" />
            ) : (
              <ChevronRight className="h-4 w-4 text-muted-foreground" />
            ))}
        </button>
        {onEditName && !section.generated && (
          <EditNameButton
            label={displayLabel}
            testId={`button-edit-name-section-${section.id}`}
            onEdit={() => onEditName(section.id)}
          />
        )}
      </div>
      {/* Grid-rows trick animates to the content's natural height, so long names wrap without clipping. */}
      <div
        className="grid transition-[grid-template-rows,opacity] duration-200 ease-in-out"
        style={{ gridTemplateRows: isExpanded ? "1fr" : "0fr", opacity: isExpanded ? 1 : 0 }}
        aria-hidden={!isExpanded}
        ref={(el) => {
          if (!el) return;
          if (isExpanded) el.removeAttribute("inert");
          else el.setAttribute("inert", "");
        }}
      >
       <div className="overflow-hidden min-h-0">
        {section.items.map((item) => {
          const ItemIcon = item.icon;
          const isSelected = selectedItemId === item.id;
          const itemLabel = getNavItemDisplayLabel(section.id, item, headingNames);
          return (
            <div key={item.id} className={`flex items-start ${isSelected ? "bg-primary/10" : ""}`}>
            <button
              onClick={() => onSelectItem(item, section.id)}
              className={`flex-1 min-w-0 flex items-center gap-3 pl-8 pr-2 py-2.5 text-left transition-colors ${
                isSelected
                  ? "text-foreground font-medium"
                  : "hover-elevate text-muted-foreground"
              }`}
              data-testid={`button-nav-item-${item.id}`}
            >
              <ItemIcon
                className={`h-4 w-4 flex-shrink-0 ${
                  isSelected ? "text-primary" : "text-muted-foreground"
                }`}
              />
              <span className="text-sm min-w-0 break-words [overflow-wrap:anywhere] leading-snug">{itemLabel}</span>
            </button>
            {onEditName && !item.generated && (
              <EditNameButton
                label={itemLabel}
                testId={`button-edit-name-item-${item.id}`}
                onEdit={() => onEditName(section.id, item.id)}
              />
            )}
            </div>
          );
        })}
       </div>
      </div>
    </div>
  );
}

export function CategorySidebar({
  sections,
  sidebarSearch,
  setSidebarSearch,
  expandedSections,
  toggleSection,
  selectedItemId,
  onSelectItem,
  onSelectSection,
  onDragEnd,
  headingNames,
  onEditName,
}: {
  headingNames?: Record<string, string> | null;
  onEditName?: (sectionId: string, itemId?: string) => void;
  sections: CostNavSection[];
  sidebarSearch: string;
  setSidebarSearch: (v: string) => void;
  expandedSections: Set<string>;
  toggleSection: (id: string) => void;
  selectedItemId: string | null;
  onSelectItem: (item: CostNavItem, sectionId: string) => void;
  onSelectSection: (section: CostNavSection) => void;
  onDragEnd: (activeId: string, overId: string) => void;
}) {
  const isSearching = sidebarSearch.trim().length > 0;

  const filteredSections = useMemo(() => {
    if (!isSearching) return sections;
    const q = sidebarSearch.toLowerCase();
    return sections
      .map((section) => {
        // Match canonical label OR the displayed custom name. The alias is only
        // compared as text; the returned section keeps its original id/label.
        const shown = getNavSectionDisplayLabel(section, headingNames).toLowerCase();
        const sectionMatches = section.label.toLowerCase().includes(q) || shown.includes(q);
        const matchingItems = section.items.filter(
          (item) =>
            sectionMatches ||
            item.label.toLowerCase().includes(q) ||
            getNavItemDisplayLabel(section.id, item, headingNames).toLowerCase().includes(q),
        );
        if (matchingItems.length > 0 || sectionMatches) {
          return {
            ...section,
            items: sectionMatches
              ? section.items
              : matchingItems,
          };
        }
        return null;
      })
      .filter(Boolean) as CostNavSection[];
  }, [sections, sidebarSearch, isSearching, headingNames]);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      onDragEnd(active.id as string, over.id as string);
    }
  }

  const sectionIds = filteredSections.map((s) => s.id);

  return (
    <div className="flex flex-col h-full" data-testid="sidebar-cost-nav">
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search categories..."
            value={sidebarSearch}
            onChange={(e) => setSidebarSearch(e.target.value)}
            className="pl-9"
            data-testid="input-sidebar-search"
          />
        </div>
      </div>
      <ScrollArea className="flex-1">
        <div className="py-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={sectionIds} strategy={verticalListSortingStrategy}>
              {filteredSections.map((section) => (
                <SortableSectionItem
                  key={section.id}
                  section={section}
                  displayLabel={getNavSectionDisplayLabel(section, headingNames)}
                  isExpanded={expandedSections.has(section.id) || isSearching}
                  isSearching={isSearching}
                  selectedItemId={selectedItemId}
                  onToggle={() => toggleSection(section.id)}
                  onSelectSection={onSelectSection}
                  onSelectItem={onSelectItem}
                  headingNames={headingNames}
                  onEditName={onEditName}
                />
              ))}
            </SortableContext>
          </DndContext>
          {filteredSections.length === 0 && isSearching && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              No categories match your search
            </div>
          )}
        </div>
      </ScrollArea>
    </div>
  );
}
